import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { asRecord, readString } from '../../utils';
import { Field } from './editor-field';
import {
  buildMapperNodeData,
  readMappingRows,
  type MappingRow,
} from './mapper-node-data';
import { buildUpstreamSources } from './variable-picker-data';
import { TokenInput } from './tiptap/token-input';
import type { WorkflowNodeEditorProps } from './types';

const { readMapperOutputMode, readMapperRowKey, MAPPER_DEFAULT_ROW_KEY } =
  PersistedWorkflow;

type MapperOutputMode = PersistedWorkflow.MapperOutputMode;

const OUTPUT_MODE_LABELS: Record<MapperOutputMode, string> = {
  record: 'Object — one property per field',
  row: 'List — text values in order, e.g. a spreadsheet row',
};

function isMapperOutputMode(value: string): value is MapperOutputMode {
  return value === 'record' || value === 'row';
}

/**
 * Config editor for a mapper node: a name, plus the fields it publishes.
 *
 * Every field's value is picked from ANY earlier step — the trigger included —
 * which is the whole reason to reach for this node: the step below it can then
 * read what it needs as its own plain input rather than having nowhere to
 * declare a reference.
 */
export const MapperNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const data = asRecord(node.data);
  const seeded = readMappingRows(data);
  const [name, setName] = useState<string>(readString(data, 'name') ?? '');
  const [output, setOutput] = useState<MapperOutputMode>(
    readMapperOutputMode(data)
  );
  const [rowKey, setRowKey] = useState<string>(
    readString(data, 'rowKey') ?? ''
  );
  const [rows, setRows] = useState<MappingRow[]>(seeded.rows);
  const [nextRowId, setNextRowId] = useState(seeded.nextId);

  const [seededNodeId, setSeededNodeId] = useState(node.id);
  if (seededNodeId !== node.id) {
    setSeededNodeId(node.id);
    const next = asRecord(node.data);
    const nextRows = readMappingRows(next);
    setName(readString(next, 'name') ?? '');
    setOutput(readMapperOutputMode(next));
    setRowKey(readString(next, 'rowKey') ?? '');
    setRows(nextRows.rows);
    setNextRowId(nextRows.nextId);
  }

  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  const sources = useMemo(
    () => buildUpstreamSources(upstreamSamples),
    [upstreamSamples]
  );

  useEffect(() => {
    onChange({
      type: 'mapper',
      data: buildMapperNodeData({ name, output, rowKey, rows }),
    });
  }, [name, output, rowKey, rows, onChange]);

  const updateRow = (
    id: string,
    patch: Partial<Pick<MappingRow, 'key' | 'value'>>
  ): void => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const moveRow = (id: string, delta: -1 | 1): void => {
    setRows((prev) => {
      const from = prev.findIndex((row) => row.id === id);
      const to = from + delta;
      const moving = prev[from];
      if (moving === undefined || to < 0 || to >= prev.length) {
        return prev;
      }
      const next = prev.filter((row) => row.id !== id);
      next.splice(to, 0, moving);
      return next;
    });
  };

  const addRow = (): void => {
    const id = `row-${nextRowId}`;
    setNextRowId((n) => n + 1);
    setRows((prev) => [...prev, { id, key: '', value: '' }]);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          Name
        </Text.Caption>
        <Input
          value={name}
          placeholder="Mapper"
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <Field label="Output">
        <Select
          value={output}
          onValueChange={(next) => {
            if (isMapperOutputMode(next)) {
              setOutput(next);
            }
          }}
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(['record', 'row'] as const).map((option) => (
              <SelectItem key={option} value={option}>
                {OUTPUT_MODE_LABELS[option]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {output === 'row' ? (
        <Field
          label="List name"
          hint={
            <>
              The output is{' '}
              <span className="font-mono">
                {`{ "${readMapperRowKey({ rowKey })}": [ … ] }`}
              </span>
              . Bind it to a spreadsheet step&apos;s row values.
            </>
          }
        >
          <Input
            value={rowKey}
            placeholder={MAPPER_DEFAULT_ROW_KEY}
            onChange={(event) => setRowKey(event.target.value)}
          />
        </Field>
      ) : null}

      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          {output === 'row' ? 'Values' : 'Output fields'}
        </Text.Caption>
        <Text.Caption className="text-muted-foreground/80 block">
          {output === 'row' ? (
            <>
              Each value becomes one item in the list, top to bottom, as text —
              lists and objects as JSON, empty values left empty. Type{' '}
              <span className="font-mono">/</span> to pull a value from any
              earlier step or the trigger.
            </>
          ) : (
            <>
              Each field becomes a top-level property of this step&apos;s
              output. Type <span className="font-mono">/</span> to pull a value
              from any earlier step or the trigger. A field holding one variable
              and nothing else keeps that value&apos;s type; mix in text and it
              becomes a sentence.
            </>
          )}
        </Text.Caption>
        <div className="flex flex-col gap-3">
          {rows.map((row, index) => (
            <div
              key={row.id}
              className="border-input flex flex-col gap-2 rounded-md border p-3"
            >
              <div className="flex items-center gap-2">
                {output === 'row' ? (
                  <Text.Caption className="text-muted-foreground flex-1">
                    Value {index + 1}
                  </Text.Caption>
                ) : (
                  <Input
                    value={row.key}
                    placeholder="Field name"
                    className="h-8 flex-1"
                    onChange={(event) =>
                      updateRow(row.id, { key: event.target.value })
                    }
                  />
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  title="Move up"
                  disabled={index === 0}
                  onClick={() => moveRow(row.id, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  title="Move down"
                  disabled={index === rows.length - 1}
                  onClick={() => moveRow(row.id, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  title="Remove field"
                  onClick={() => removeRow(row.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <TokenInput
                value={row.value}
                placeholder="Type / to insert a value from an earlier step"
                sources={sources}
                onChange={(template) => updateRow(row.id, { value: template })}
              />
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-9 justify-start gap-2"
          onClick={addRow}
        >
          <Plus className="h-4 w-4" />
          {output === 'row' ? 'Add value' : 'Add field'}
        </Button>
      </div>
    </div>
  );
};
